frappe.provide("eternal_support");

eternal_support.chunks = [];
eternal_support.blob = null;
eternal_support.blobURL = null;

eternal_support.SupportTicket = class SupportTicket {
	constructor() {
		const boot = frappe.boot.eternal_support || {};
		if (!boot.enabled) {
			frappe.show_alert(
				{
					indicator: "orange",
					message: __("Support ticket experience is disabled for this site."),
				},
				5
			);
			return;
		}

		this.brand = boot;
		this.formSchema = [];
		this.init_config();
		this.initialize();
	}

	async initialize() {
		await this.fetchSchema();
		this.setup_dialog();
		this.dialog.show();
	}

	init_config() {
		this.stream = null;
		this.audio = null;
		this.mixedStream = null;
		this.recorder = null;
		this.recordedVideo = null;
		this.recordingStart = null;
		this.inUpload = false;
		this.attachments = [];

		eternal_support.chunks = [];
		eternal_support.blob = null;
		eternal_support.blobURL = null;

		this.maxFileSizeInBytes = (this.brand.max_file_size || 50) * 1024 * 1024;
		this.sizeWarning = (this.brand.max_file_size || 50) * 0.25 * 1024 * 1024;
		this.nextWarningSize = this.sizeWarning;
	}

	async fetchSchema() {
		try {
			const { message } = await frappe.call({
				method: "eternal_support.utils.support.get_ticket_form_schema",
				freeze: false,
			});
			this.formSchema = (message && message.fields) || [];
		} catch (error) {
			console.error("[Eternal Support] Unable to load ticket schema", error);
			this.formSchema = [];
		}

		if (!this.formSchema.length) {
			this.formSchema = this.getFallbackSchema();
		}
	}

	setup_dialog() {
		this.dialog = new frappe.ui.Dialog({
			title: __("Reach Eternal Support"),
			size: "extra-large",
			static: true,
			minimizable: true,
			fields: this.getFields(),
			primary_action_label: this.brand.cta_label || __("Send to Eternal Support"),
			primary_action: (values) => this.raise_ticket(values),
			secondary_action_label: __("Close"),
			secondary_action: () => this.handleDialogClose(),
		});

		this.dialog.$wrapper.find(".modal-dialog").addClass("eternal-support-modal");
		const minimize_btn = this.dialog.get_minimize_btn().addClass("pr-3");
		this.injectCloseButton(minimize_btn);

		this.renderHero();
		this.renderAttachmentList();
		this.renderRecordingStatus();
	}

	injectCloseButton(minimize_btn) {
		const closeHandler = () => this.handleDialogClose();
		const close_btn = $(
			`<button type="button" class="btn btn-default btn-modal-close" title="${__("Close")}">
				<i class="fa fa-times"></i>
			</button>`
		);
		close_btn.on("click", closeHandler);
		minimize_btn.after(close_btn);
	}

	handleDialogClose() {
		if (this.recorder && this.recorder.state === "recording") {
			this.showAlert("red", __("Stop the recording before closing."));
			return;
		}
		this.dialog.hide();
	}

	getFields() {
		const fields = [
			{ fieldtype: "HTML", fieldname: "hero_block" },
			{ fieldtype: "Section Break", label: __("Basic Details") }, // <-- ADD THIS
		];
		let activeSection = "Basic Details"; // <-- initialize to skip duplicate

		this.formSchema.forEach((schemaField) => {
			if (schemaField.section_label && schemaField.section_label !== activeSection) {
				fields.push({
					fieldtype: "Section Break",
					label: schemaField.section_label,
				});
				activeSection = schemaField.section_label;
			}

			if (schemaField.column_break) {
				fields.push({ fieldtype: "Column Break" });
			}

			fields.push(this.getDialogFieldDef(schemaField));
		});

		fields.push(
			{
				fieldname: "share_context",
				label: __("Capture current page context"),
				fieldtype: "Check",
				default: 1,
				hidden: 1,
			},
			{
				fieldtype: "Section Break",
				label: __("Attachments & Recording"),
			},
			{ fieldname: "recording_status", fieldtype: "HTML" },
			{
				fieldname: "record_screen",
				label: __("Start Recording"),
				fieldtype: "Button",
				click: () => this.toggleRecording(),
			},
			{
				fieldname: "view_recording",
				label: __("Preview"),
				fieldtype: "Button",
				hidden: 1,
				click: () => this.openRecording(),
			},
			{
				fieldname: "clear_recording",
				label: __("Clear Recording"),
				fieldtype: "Button",
				hidden: 1,
				click: () => this.resetRecording(),
			},
			{ fieldtype: "Column Break" },
			{
				fieldname: "upload_attachment",
				label: __("Add Attachment"),
				fieldtype: "Button",
				click: () => this.launchUploader(),
			},
			{ fieldname: "attachments_preview", fieldtype: "HTML" }
		);

		return fields;
	}

	renderHero() {
		const boot = this.brand;
		const heroTitle = boot.hero_title || __("Facing an issue?");
		const heroSubtitle =
			boot.hero_subtitle ||
			__(
				"Record your screen, add context, and dispatch it to Eternal Support without leaving ERPNext."
			);
		const primary = boot.brand_color || "#1c4ed8";
		const secondary = boot.brand_secondary_color || "#38bdf8";
		const logoUrl = this.getBrandLogo();

		const defaultFeatureList = `
			<ul>
				<li>${__("Recording + metadata in a single submission")}</li>
				<li>${__("Route automatically to HD Ticket doctype")}</li>
				<li>${__("We auto-sync status back to your issue queue")}</li>
			</ul>
		`;
		const featureListHTML = this.brand.feature_list || defaultFeatureList;

		const heroHTML = `
			<style>
				.eternal-support-hero {
					border-radius: 12px;
					padding: 18px 24px;
					color: #fff;
					margin-bottom: 12px;
					display: flex;
					flex-direction: column;
					gap: 12px;
				}
				.eternal-support-hero__head {
					display: flex;
					align-items: center;
					gap: 16px;
				}
				.eternal-support-hero__logo img {
					height: 48px;
					width: auto;
				}
				.eternal-support-hero h3 { margin: 0; font-size: 1.4rem; }
				.eternal-support-hero ul { margin: 0; padding-left: 18px; }
			</style>
			<div class="eternal-support-hero" style="background: linear-gradient(120deg, ${primary}, ${secondary});">
				<div class="eternal-support-hero__head">
					${
						logoUrl
							? `<div class="eternal-support-hero__logo"><img src="${logoUrl}" alt="${heroTitle}"/></div>`
							: ""
					}
					<div class="eternal-support-hero__text">
						<h3>${heroTitle}</h3>
						<p>${heroSubtitle}</p>
					</div>
				</div>
				<div class="eternal-support-features">
					${featureListHTML}
				</div>
			</div>
		`;
		this.dialog.get_field("hero_block").$wrapper.html(heroHTML);
	}

	renderAttachmentList() {
		const wrapper = this.dialog.get_field("attachments_preview").$wrapper;
		if (!this.attachments.length) {
			wrapper.html(`<div class="text-muted">${__("No attachments yet.")}</div>`);
			return;
		}

		const pillStyle =
			"display:flex;align-items:center;justify-content:space-between;gap:8px;background:#f4f4f5;border-radius:999px;padding:6px 12px;margin-bottom:6px;";
		const items = this.attachments
			.map(
				(file, idx) =>
					`<div class="attachment-pill" style="${pillStyle}"><span>${
						file.file_name || file.file_url
					}</span><button class="btn btn-xs btn-default" data-index="${idx}">${__(
						"Remove"
					)}</button></div>`
			)
			.join("");
		wrapper.html(`<div class="eternal-attachments">${items}</div>`);
		wrapper.find("button").on("click", (e) => {
			const index = $(e.currentTarget).data("index");
			this.attachments.splice(index, 1);
			this.renderAttachmentList();
		});
	}

	renderRecordingStatus(label) {
		const wrapper = this.dialog.get_field("recording_status").$wrapper;
		const state =
			label || __("Recording is idle. Capture a quick walkthrough for the support team.");
		wrapper.html(`<div class="eternal-recording-status">${state}</div>`);
	}

	launchUploader() {
		new frappe.ui.FileUploader({
			allow_multiple: false,
			on_success: (file_doc) => {
				this.attachments.push({
					file_url: file_doc.file_url,
					file_name: file_doc.file_name,
				});
				this.renderAttachmentList();
			},
			on_error: () => this.showAlert("red", __("Failed to attach file")),
		});
	}

	toggleRecording() {
		if (this.recorder && this.recorder.state === "recording") {
			this.stopRecording();
			return;
		}
		this.startRecording();
	}

	async startRecording() {
		if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
			this.showAlert("red", __("Screen recording is not supported in this browser."));
			return;
		}
		try {
			this.stream = await navigator.mediaDevices.getDisplayMedia({
				video: {
					width: { max: 1920 },
					height: { max: 1080 },
					frameRate: 30,
				},
				audio: true,
			});
			this.audio = await navigator.mediaDevices.getUserMedia({
				audio: true,
			});
			const tracks = [...this.stream.getTracks(), ...this.audio.getTracks()];
			this.mixedStream = new MediaStream(tracks);
			this.recorder = new MediaRecorder(this.mixedStream, {
				mimeType: "video/webm",
			});
			this.recorder.ondataavailable = (e) => this.captureChunk(e);
			this.recorder.start(200);
			this.recordingStart = Date.now();
			this.dialog.set_df_property("record_screen", "label", __("Stop Recording"));
			this.renderRecordingStatus(__("Recording in progress..."));
		} catch (error) {
			console.error(error);
			this.showAlert("red", __("Screen recording permission was denied."));
		}
	}

	captureChunk(event) {
		if (!event.data || !event.data.size) return;
		eternal_support.chunks.push(event.data);
		const size = eternal_support.chunks.reduce((acc, chunk) => acc + chunk.size, 0);
		if (size >= this.nextWarningSize) {
			this.showAlert(
				"orange",
				__("Recording file is getting heavy (~{0} MB)", [(size / 1024 / 1024).toFixed(1)])
			);
			this.nextWarningSize += this.sizeWarning;
		}
		if (size > this.maxFileSizeInBytes) {
			this.showAlert("red", __("Recording stopped to keep file size manageable."));
			this.stopRecording();
		}
	}

	stopRecording() {
		if (!this.recorder) return;
		this.recorder.stop();
		this.stream.getTracks().forEach((track) => track.stop());
		if (this.audio) {
			this.audio.getTracks().forEach((track) => track.stop());
		}
		eternal_support.blob = new Blob(eternal_support.chunks, {
			type: "video/webm",
		});
		eternal_support.blobURL = URL.createObjectURL(eternal_support.blob);
		this.dialog.set_df_property("record_screen", "label", __("Re-record"));
		this.dialog.set_df_property("view_recording", "hidden", 0);
		this.dialog.set_df_property("clear_recording", "hidden", 0);
		this.renderRecordingStatus(__("Recording ready. Preview or clear before sending."));
	}

	openRecording() {
		if (eternal_support.blobURL) {
			window.open(eternal_support.blobURL, "_blank");
		}
	}

	resetRecording() {
		if (this.recorder && this.recorder.state === "recording") {
			this.showAlert("red", __("Stop the recording before clearing."));
			return;
		}
		eternal_support.chunks = [];
		eternal_support.blob = null;
		eternal_support.blobURL = null;
		this.dialog.set_df_property("record_screen", "label", __("Start Recording"));
		this.dialog.set_df_property("view_recording", "hidden", 1);
		this.dialog.set_df_property("clear_recording", "hidden", 1);
		this.renderRecordingStatus();
	}

	showAlert(indicator, message) {
		frappe.show_alert({ indicator, message });
	}

	captureContext() {
		const route =
			(typeof frappe.get_route_str === "function" && frappe.get_route_str()) ||
			window.location.hash ||
			window.location.pathname;
		const doctype =
			(window.cur_list && window.cur_list.doctype) ||
			(window.cur_frm && window.cur_frm.doctype);
		const docname = window.cur_frm && window.cur_frm.doc ? window.cur_frm.doc.name : undefined;
		return {
			route,
			doctype,
			docname,
			user: frappe.session.user,
			browser: window.navigator.userAgent,
			timestamp: frappe.datetime ? frappe.datetime.now_datetime() : new Date().toISOString(),
		};
	}

	async raise_ticket(values) {
		if (this.recorder && this.recorder.state === "recording") {
			this.showAlert("red", __("Stop the recording before sending."));
			return;
		}
		if (this.inUpload) return;

		this.inUpload = true;
		let recording_path = null;
		if (eternal_support.blob) {
			const filename = `ES_${frappe.session.user}_${frappe.datetime
				.now_datetime()
				.replace(/[: ]/g, "-")}.webm`;
			recording_path = await eternal_support.uploadFile(eternal_support.blob, filename, {
				start_label: __("Uploading recording"),
			});
			if (!recording_path) {
				this.inUpload = false;
				return;
			}
		}

		const additional_files = this.attachments.map((f) => f.file_url).filter(Boolean);
		const ticket_doc = this.prepareTicketDoc(values);
		const share_context = values.share_context === undefined ? 1 : values.share_context;
		const meta = {
			share_context: share_context || 0,
			context_snapshot: share_context ? this.captureContext() : null,
		};

		frappe.call({
			method: "eternal_support.utils.support.create_support_ticket",
			type: "POST",
			args: {
				title: ticket_doc.issue_title,
				description: ticket_doc.description,
				meta: JSON.stringify(meta),
				attachments: JSON.stringify(additional_files),
				screen_recording: recording_path,
				ticket_fields: JSON.stringify(ticket_doc),
			},
			freeze: true,
			freeze_message: __("Creating support ticket..."),
			callback: (r) => {
				this.inUpload = false;
				if (!r.exc && r.message) {
					const ticketName = r.message.issue || r.message.ticket;
					const ticketUrl = `/app/eternal-support-ticket/${encodeURIComponent(
						ticketName
					)}`;
					this.dialog.hide();
					frappe.show_alert(
						{
							indicator: "green",
							message: __(
								'<strong>New Ticket <a href="{0}">#{1}</a> has been created successfully.</strong><br>Kindly contact your IT/Admin or Support Team to review and approve the ticket so that it can be forwarded to the Eternal Team for further action.',
								[ticketUrl, frappe.utils.escape_html(ticketName)]
							),
						},
						10
					);
				}
			},
			always: () => (this.inUpload = false),
		});
	}

	getDialogFieldDef(field) {
		const mandatoryFields = new Set(["ticket_type", "priority", "impact", "raised_by"]);
		const dialogField = {
			fieldname: field.fieldname,
			label: field.label,
			fieldtype: this.mapFieldtype(field.fieldtype),
			reqd: mandatoryFields.has(field.fieldname) ? 1 : field.reqd,
			default: field.default,
			description: field.description,
			placeholder: field.placeholder,
		};

		if (dialogField.fieldname === "raised_by" && frappe.session.user !== "Guest") {
			dialogField.default = frappe.session.user_email;
			dialogField.read_only = 1;
		}

		if (dialogField.fieldtype === "Select" && Array.isArray(field.options)) {
			dialogField.options = field.options.join("\n");
		} else if (field.options) {
			dialogField.options = field.options;
		}

		if (dialogField.fieldtype === "Check" && dialogField.default === undefined) {
			dialogField.default = 0;
		}

		return dialogField;
	}

	mapFieldtype(fieldtype) {
		if (fieldtype === "Text") {
			return "Small Text";
		}
		return fieldtype;
	}

	prepareTicketDoc(values) {
		const doc = {};
		this.formSchema.forEach((field) => {
			doc[field.fieldname] = values[field.fieldname];
		});

		if (!doc.issue_title) {
			doc.issue_title = __("Support Request");
		}
		if (!doc.description) {
			doc.description = "";
		}
		if (!doc.raised_by && frappe.session.user_email) {
			doc.raised_by = frappe.session.user_email;
		}

		return doc;
	}

	getFallbackSchema() {
		const isGuest = frappe.session.user === "Guest" || !frappe.session.user;
		const userEmail = frappe.session.user_email || frappe.session.user || "";

		return [
			{
				fieldname: "ticket_type",
				label: __("Ticket Type"),
				fieldtype: "Link",
				options: "Eternal Support Ticket Type",
				section_label: __("Basic Details"),
			},
			{
				fieldname: "priority",
				label: __("Priority"),
				fieldtype: "Select",
				options: "\nLow\nMedium\nHigh\nUrgent",
			},
			{
				fieldname: "impact",
				label: __("Impact"),
				fieldtype: "Select",
				column_break: 1,
				options: "\nSingle User\nTeam\nBusiness Unit\nOrganization",
			},
			{
				fieldname: "raised_by",
				label: __("Contact Email"),
				fieldtype: "Data",
				default: isGuest ? "" : userEmail,
				reqd: isGuest ? 1 : 0,
			},
			{
				fieldname: "issue_title",
				label: __("Title"),
				fieldtype: "Data",
				reqd: 1,
				section_label: __("Ticket Basics"),
			},
			{
				fieldname: "description",
				label: __("Describe what happened"),
				fieldtype: "Text Editor",
				reqd: 1,
				section_label: __("Details"),
			},
		];
	}

	getBrandLogo() {
		const defaultLogo = "/public/images/Eternal-tech-favicon.png";
		const logoUrl = this.brand.logo || defaultLogo;

		if (!logoUrl) return "";
		if (logoUrl.startsWith("http")) {
			return logoUrl;
		}
		return `${window.location.origin}${logoUrl}`;
	}
};
